import type { schedule as SCH } from '@cwos/api-client';

/**
 * COVERAGE — the decisions behind "can this worker take this work, is this
 * client authorized to receive it, and is anything blocking either".
 *
 * Everything here is pure and separately testable, and it is separate from the
 * panel for one reason: three of these functions are the difference between a
 * scheduler making an offer that will hold and one that will be refused, and a
 * decision buried in JSX cannot be mutated to see whether anything is watching.
 *
 * ---------------------------------------------------------------------------
 * 1. AN ELIGIBILITY HOLD MUST REACH THE MOMENT OF OFFERING
 * ---------------------------------------------------------------------------
 * `domains/eligibility` places holds; `eligibilityGuard` refuses eight route
 * families for a held worker. Until recently the coverage engine did not
 * consult it, so a held worker could be ranked first and offered a shift they
 * would then be refused. That is now fixed at the source — `buildCoverage`
 * batches `activeHoldsForWorkers` and every candidate carries the answer.
 *
 * This module does NOT trust that and re-derive nothing: it reads the criterion
 * the server sent. But it does REFUSE INDEPENDENTLY. `offerableAudience` drops
 * a held candidate from the audience even if the server put them in it, because
 * the consequence of the two disagreeing lands on a person: an offer that will
 * be refused wastes a scheduler's time and looks to the worker like the system
 * changed its mind. A screen whose only defence is that the server is currently
 * correct is one deploy away from making that offer again.
 *
 * ---------------------------------------------------------------------------
 * 2. `unknown` IS NOT `pass`
 * ---------------------------------------------------------------------------
 * `Verdict` has four values and the matcher means them. A criterion that could
 * not run reports `unknown`, and `eligibilityStanding` keeps that as its own
 * answer — "we did not check" — rather than folding it into either verdict.
 * `CoverageResult.eligibilityUnchecked` is the same distinction over the whole
 * result and is rendered as a warning, never as silence.
 *
 * ---------------------------------------------------------------------------
 * 3. `status: 'active'` IS NOT "AUTHORIZED TODAY"
 * ---------------------------------------------------------------------------
 * `service_authorization.status` is only ever written `'active'` or
 * `'revoked'`. The migration says so out loud — a ceiling "EXPIRES (ends_on
 * passes) or is REVOKED (status)" — so nothing ever transitions a row to
 * `'expired'` and the stored status distinguishes live from revoked and nothing
 * else. Whether a ceiling is IN FORCE is a question about `startsOn`/`endsOn`
 * against TODAY IN THE ORGANIZATION'S ZONE, and `authorizationStanding` is the
 * only place this app answers it.
 *
 * The trap this exists to avoid is the one that bit three fixtures this week:
 * "recent" is not "inside the current period". A ceiling issued last month is
 * recent; if it ended last Friday it authorises nothing today.
 */

export type CoverageCriterionKey = SCH.CoverageCriterionKey;

/** The one criterion whose failure this module treats as a hold. */
const ELIGIBILITY: CoverageCriterionKey = 'eligibility';

/**
 * What the eligibility check said about one candidate.
 *
 *   'clear'      — evaluated, and no active hold applies.
 *   'held'       — evaluated, and a hold applies. NEVER offer.
 *   'unchecked'  — the eligibility domain was not consulted. Not a pass.
 *   'absent'     — the server sent no eligibility criterion at all. Treated
 *                  exactly as `unchecked`: a missing answer is not a good one.
 */
export type EligibilityStanding = 'clear' | 'held' | 'unchecked' | 'absent';

export function eligibilityStanding(rec: SCH.Recommendation): EligibilityStanding {
  const criterion = rec.criteria.find((c) => c.key === ELIGIBILITY);
  if (criterion === undefined) return 'absent';
  switch (criterion.verdict) {
    case 'fail':
      return 'held';
    case 'pass':
      return 'clear';
    // `unknown` and `not_applicable` both mean the check did not produce a
    // verdict about this person. Neither is permission to offer.
    default:
      return 'unchecked';
  }
}

/** True only for a candidate the eligibility domain positively cleared. */
export function isOfferable(rec: SCH.Recommendation): boolean {
  return !rec.excluded && eligibilityStanding(rec) === 'clear';
}

/**
 * WHO MAY ACTUALLY BE OFFERED THIS SHIFT.
 *
 * Derived from `considered`, not read off `audience`. The server builds
 * `audience` by dropping excluded candidates, and today it also marks a held
 * worker excluded — so on a correct server these two agree exactly. They are
 * computed separately anyway, and `heldInServerAudience` below reports when
 * they do not, because the failure this guards against is precisely the one
 * where the server stops agreeing.
 */
export function offerableAudience(coverage: SCH.CoverageResult): SCH.Recommendation[] {
  return coverage.considered.filter(isOfferable);
}

/**
 * Anybody the SERVER put in the offer audience who is on a hold.
 *
 * Non-empty means the two sides disagree about who may be offered work, and the
 * screen says so rather than quietly rendering the smaller list. A silent
 * correction here would hide exactly the regression that produced the original
 * defect.
 */
export function heldInServerAudience(coverage: SCH.CoverageResult): SCH.Recommendation[] {
  return coverage.audience.filter((r) => eligibilityStanding(r) === 'held');
}

/** Everybody the eligibility domain says is on a hold, offered or not. */
export function heldCandidates(coverage: SCH.CoverageResult): SCH.Recommendation[] {
  return coverage.considered.filter((r) => eligibilityStanding(r) === 'held');
}

/**
 * May the offer button do anything, and if not, what does the reader get told?
 *
 * `blocked` is deliberately separate from `empty`: "nobody is free" and "the
 * only people free are on holds" are different operational facts and lead a
 * scheduler to different next actions.
 */
export type OfferReadiness =
  | { kind: 'ready'; count: number }
  /** Candidates exist, and every one of them is held or excluded. */
  | { kind: 'blocked'; heldCount: number; consideredCount: number }
  /** Nobody was even considered. */
  | { kind: 'no_pool' }
  /**
   * There are offerable candidates but eligibility was never checked for
   * anybody. The offer is allowed — refusing it would make the product
   * unusable when the eligibility domain is unreachable — and the warning
   * travels with it.
   */
  | { kind: 'unverified'; count: number };

export function offerReadiness(coverage: SCH.CoverageResult): OfferReadiness {
  if (coverage.considered.length === 0) return { kind: 'no_pool' };
  const offerable = offerableAudience(coverage);
  if (offerable.length > 0) return { kind: 'ready', count: offerable.length };
  // Nobody positively cleared. If the reason is that nothing was checked, say
  // that; a scheduler who is told "everyone is on hold" when nobody was asked
  // will go and look for holds that do not exist.
  const unchecked = coverage.considered.filter(
    (r) => !r.excluded && eligibilityStanding(r) !== 'held',
  );
  if (unchecked.length > 0) return { kind: 'unverified', count: unchecked.length };
  return {
    kind: 'blocked',
    heldCount: heldCandidates(coverage).length,
    consideredCount: coverage.considered.length,
  };
}

/**
 * The sentence under a candidate who will not be offered the shift.
 *
 * A hold outranks every other exclusion, because it is the one a scheduler
 * cannot work around by moving hours about.
 */
export function exclusionReading(rec: SCH.Recommendation): string | null {
  if (eligibilityStanding(rec) === 'held') {
    return 'On an eligibility hold — this person would be refused the shift.';
  }
  if (!rec.excluded) return null;
  return rec.exclusionReason ?? 'Excluded by the coverage check.';
}

// ---------------------------------------------------------------------------
// Dated facts
// ---------------------------------------------------------------------------

/**
 * Today's calendar date IN A NAMED ZONE, as `YYYY-MM-DD`.
 *
 * `en-CA` because its short date format IS ISO order; building the string from
 * `getFullYear()` and friends would read the DEVICE's date, which is the wrong
 * day for any viewer far enough from the organization — and "is this
 * authorization in force" is exactly the question where being a day out is a
 * billing answer rather than a cosmetic one.
 */
export function localDateIn(timezone: string, now: Date): string {
  // One formatter per zone: constructing an Intl.DateTimeFormat costs ~25µs,
  // and the Schedule's Month view calls this once per visit per render.
  let fmt = DATE_IN_ZONE.get(timezone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    DATE_IN_ZONE.set(timezone, fmt);
  }
  return fmt.format(now);
}
const DATE_IN_ZONE = new Map<string, Intl.DateTimeFormat>();

/**
 * Where a dated fact stands relative to a day.
 *
 *   'revoked'   — a person ended it. Outranks every date.
 *   'not_yet'   — it starts in the future.
 *   'ended'     — its window closed. STILL A RECORD, never a deletion.
 *   'in_force'  — the day falls inside the window.
 *   'undated'   — no start and no end were recorded, so it has always applied
 *                 and still does. Distinct from `in_force`, because "no window
 *                 was ever written down" is a data-quality fact a payer audit
 *                 will ask about and "the window covers today" is not.
 */
export type DatedStanding = 'revoked' | 'not_yet' | 'ended' | 'in_force' | 'undated';

export function authorizationStanding(
  auth: Pick<SCH.AuthorizationView, 'status' | 'startsOn' | 'endsOn'>,
  /** Today in the ORGANIZATION's zone, `YYYY-MM-DD`. */
  today: string,
): DatedStanding {
  if (auth.status === 'revoked') return 'revoked';
  // 'expired' is in the type because the CHECK constraint allows it. Nothing
  // writes it today; if something ever does, it means the same as `ended` and
  // is honoured rather than silently read as live.
  if (auth.status === 'expired') return 'ended';
  if (auth.startsOn !== null && today < auth.startsOn) return 'not_yet';
  if (auth.endsOn !== null && today > auth.endsOn) return 'ended';
  if (auth.startsOn === null && auth.endsOn === null) return 'undated';
  return 'in_force';
}

export const DATED_STANDING_READING: Record<DatedStanding, string> = {
  in_force: 'In force',
  not_yet: 'Starts later',
  ended: 'Window has passed',
  revoked: 'Revoked',
  undated: 'No dates recorded',
};

/**
 * Whether this authorization limits work happening TODAY.
 *
 * The one predicate a reader actually acts on, and the reason the whole
 * `DatedStanding` type exists rather than a boolean: the four ways of not
 * limiting today's work are four different conversations.
 */
export function limitsToday(
  auth: Pick<SCH.AuthorizationView, 'status' | 'startsOn' | 'endsOn'>,
  today: string,
): boolean {
  const standing = authorizationStanding(auth, today);
  return standing === 'in_force' || standing === 'undated';
}

export type CeilingCheckReading = {
  text: string;
  pill: {
    label: string;
    variant: 'urgent' | 'good' | 'unsure';
  };
};

export function ceilingCheckReading(check: SCH.CeilingCheck): CeilingCheckReading {
  if (check.exceeded === true) {
    return {
      text: `Scheduled ${check.scheduledMinutes} min against a ${check.ceilingMinutes} min ceiling — over by ${check.overageMinutes} min.`,
      pill: { label: 'Over ceiling', variant: 'urgent' },
    };
  }
  if (check.exceeded === false) {
    return {
      text: `Scheduled ${check.scheduledMinutes} min of ${check.ceilingMinutes} min.`,
      pill: { label: 'Within ceiling', variant: 'good' },
    };
  }
  return {
    text: `Not comparable: ${check.reason}`,
    pill: { label: 'Not comparable', variant: 'unsure' },
  };
}

export function disagreementLabel(kind: SCH.Disagreement['kind']): string | null {
  switch (kind) {
    case 'ceiling_below_expectation':
      return 'Ceiling below expectation';
    case 'expectations_disagree':
      return 'Expectations disagree';
    case 'ceiling_not_comparable':
      return 'Not comparable';
    case 'no_ceiling':
      return 'No ceiling';
    case 'no_expectation':
      return 'No expectation';
    default:
      return null;
  }
}

/** "1 Sep 2026 – 30 Nov 2026", "from 1 Sep 2026", "until 30 Nov 2026", or null. */
export function dateWindowReading(startsOn: string | null, endsOn: string | null): string | null {
  const from = startsOn === null ? null : formatIsoDate(startsOn);
  const to = endsOn === null ? null : formatIsoDate(endsOn);
  if (from !== null && to !== null) return `${from} – ${to}`;
  if (from !== null) return `from ${from}`;
  if (to !== null) return `until ${to}`;
  return null;
}

/**
 * A `YYYY-MM-DD` rendered without going through `Date`.
 *
 * `new Date('2026-09-01')` is UTC midnight, and formatting that in any zone
 * west of UTC prints 31 August. A date-only column has no instant in it and
 * must not be given one.
 */
export function formatIsoDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  if (y === undefined || m === undefined || d === undefined) return iso;
  const MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const monthIndex = Number(m) - 1;
  const name = MONTHS[monthIndex];
  if (name === undefined) return iso;
  return `${Number(d)} ${name} ${y}`;
}

// ---------------------------------------------------------------------------
// Availability
// ---------------------------------------------------------------------------

export const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

export function dayName(dayOfWeek: number): string {
  return DAY_NAMES[dayOfWeek] ?? `Day ${dayOfWeek}`;
}

/**
 * An availability block is a DATED fact too, and the read returns every row —
 * including blocks that ended last year. Rendering them all as "available"
 * would tell a scheduler somebody works Tuesdays when they stopped in March.
 */
export function availabilityStanding(
  block: Pick<SCH.AvailabilityBlock, 'effectiveFrom' | 'effectiveTo'>,
  today: string,
): DatedStanding {
  if (today < block.effectiveFrom) return 'not_yet';
  if (block.effectiveTo !== null && today > block.effectiveTo) return 'ended';
  return 'in_force';
}

export function currentAvailability(
  blocks: readonly SCH.AvailabilityBlock[],
  today: string,
): SCH.AvailabilityBlock[] {
  return blocks.filter((b) => availabilityStanding(b, today) === 'in_force');
}

/**
 * What a capacity answer actually says, with the three zeroes kept apart.
 *
 *   'undeclared' — the worker has stated no availability for this window. NOT
 *                  "0 hours free": nobody asked them and nobody knows.
 *   'full'       — they declared hours and every one is committed. A real
 *                  measured zero.
 *   'open'       — hours remain.
 */
export type CapacityReading = 'undeclared' | 'full' | 'open';

export function capacityReading(capacity: SCH.CapacityView): CapacityReading {
  if (capacity.declaredNothing) return 'undeclared';
  return capacity.remainingMinutes > 0 ? 'open' : 'full';
}

export const CAPACITY_READING_TEXT: Record<CapacityReading, string> = {
  undeclared:
    'No availability declared for this week. This is not the same as being fully booked — nobody has said when they can work.',
  full: 'Every declared hour this week is committed.',
  open: 'Hours remain this week.',
};

/** "6h 30m", or "0h" for a real zero. Never blank. */
export function hoursAndMinutes(totalMinutes: number): string {
  const safe = Math.max(0, Math.round(totalMinutes));
  const hours = Math.floor(safe / 60);
  const minutes = safe % 60;
  if (hours === 0) return `${minutes}m`;
  return minutes === 0 ? `${hours}h` : `${hours}h ${String(minutes).padStart(2, '0')}m`;
}

// ---------------------------------------------------------------------------
// Cancellations — DELIBERATELY ABSENT, and this is the note that says why
// ---------------------------------------------------------------------------
//
// O6 asks for the cancellation record on a cancelled shift: what happened, who
// it is attributed to, whether anybody has been asked for a reason, and whether
// it escalated. The write side is complete and the reading vocabulary was
// drafted here — and then removed, because there is nowhere to get the data.
//
// `GET /schedule/cancellations/:id` takes a CANCELLATION id. No surface in the
// product has one: a shift knows nothing about its cancellation, and there is
// no `GET /schedule/shifts/:id/cancellation`.
//
// The read itself already exists. `domains/schedule/cancellation.ts` exports
// `getCancellationForShift`, written and with ZERO callers in the whole
// repository, and `availabilityRoutes` is already registered in `server.ts`, so
// exposing it needs no shared-file change at all — one `app.get` beside the
// existing `GET /schedule/cancellations/:id`, on the same `schedule:read` guard.
//
// Shipping the vocabulary without the read would have been inert carriage: a
// value plumbed through and never read, which this codebase treats as a defect
// rather than as preparation. The gap is in this lane's report instead.

// ---------------------------------------------------------------------------
// The offers themselves, read back
// ---------------------------------------------------------------------------
//
// `POST /availability/coverage/offers` returned an `OfferView` once, at
// creation, and then the offer was unreadable for the rest of its life. A
// scheduler who closed the drawer could not find out whether anybody had
// answered, could not accept on somebody's behalf because they held no offer id,
// and could not tell an unanswered offer from a declined one. The candidate rows
// recording who was asked and why existed the whole time and no route projected
// them; `GET /availability/coverage/offers` is that route.

/**
 * What an offer IS, right now — and the four are not interchangeable.
 *
 *   'open'      out, unanswered, and acceptable.
 *   'filled'    somebody has it. The row explains who is on the shift now, which
 *               is why a filled offer is shown rather than dropped: "nobody
 *               answered" and "somebody took it" are opposite answers and a list
 *               that hid the second renders them identically.
 *   'withdrawn' the organization took it back.
 *   'expired'   the deadline passed with no answer. NOT the same as withdrawn —
 *               one is a decision and the other is a silence.
 */
export type OfferStanding = 'open' | 'filled' | 'withdrawn' | 'expired' | 'unknown';

export function offerStanding(offer: { status: string }): OfferStanding {
  switch (offer.status) {
    case 'open':
    case 'filled':
    case 'withdrawn':
    case 'expired':
      return offer.status;
    default:
      // A status the CHECK constraint grows and this file has not learned yet.
      // Rendered as unknown rather than mapped to the nearest neighbour, because
      // guessing here would state a fact about an offer nobody has decided.
      return 'unknown';
  }
}

export const OFFER_STANDING_TEXT: Record<OfferStanding, string> = {
  open: 'Out, and nobody has taken it yet.',
  filled: 'Taken. The shift has moved to whoever accepted.',
  withdrawn: 'Withdrawn by the organization before anybody answered.',
  expired: 'The deadline passed with no answer. Nobody declined it — nobody replied.',
  unknown: 'Phibian does not recognise this offer’s state, so it is not describing it.',
};

/**
 * What ONE person on an offer has said.
 *
 * `'offered'` is the only one that can still be accepted, and it is the only
 * state whose absence of an answer is honest: `'withdrawn'` means somebody else
 * took the shift and this candidacy was closed FOR them, which reads very
 * differently to a scheduler than "they said no".
 */
export const CANDIDATE_STANDING_TEXT: Readonly<Record<string, string>> = Object.freeze({
  offered: 'Asked. No answer yet.',
  accepted: 'Took it.',
  declined: 'Said no.',
  withdrawn: 'Closed — somebody else took the shift.',
});

export function candidateStandingText(status: string): string {
  return CANDIDATE_STANDING_TEXT[status] ?? `Phibian does not recognise the state “${status}”.`;
}

/**
 * May a roster holder accept this candidate onto this shift?
 *
 * BOTH conditions, because either alone is wrong. The OFFER being open does not
 * mean this person's candidacy is — that was the exact defect lane K found in
 * `acceptCoverageOffer`, which read `o.status` and never `c.status`, so a worker
 * who had declined could still be put on the shift. Asking both here means the
 * button is not offered for a request the server will refuse.
 *
 * It deliberately does NOT ask about the mode. A `manager_led` shortlist is
 * exactly the thing a roster holder accepts from; the mode restricts the
 * WORKER's claim, not this.
 */
export function canAcceptCandidate(
  offer: { status: string },
  candidate: { status: string },
): boolean {
  return offer.status === 'open' && candidate.status === 'offered';
}

/**
 * May a roster holder put this person on the worker-actionable side of this
 * offer?
 *
 * THE FOUNDER'S THIRD WORKER-REACH RULING OF 2026-09-09, as a button.
 * `manager_led` stays load-bearing — a shortlist is a deliberation, and nobody
 * on one may answer for themselves — "unless that worker is explicitly included
 * in the candidate/offer flow". This is the control that does the including,
 * one named person at a time.
 *
 * FOUR CONDITIONS, and each is a different reason the button would be a lie:
 *
 *   the offer is `manager_led` — on an `open_shift` offer every candidate is
 *       already answerable by construction, so the button would claim to change
 *       something it cannot.
 *   the offer is open — a filled, withdrawn or expired offer has nothing left
 *       for a worker to answer, and the server refuses.
 *   the candidacy is `offered` — an answered or withdrawn candidacy is a
 *       settled question; opening a door onto it asks nothing.
 *   they are not already included — the act is idempotent on the server, but a
 *       button that reads "Let X answer" beside somebody who already can is a
 *       control with no state.
 *
 * The pair with `canAcceptCandidate` is the point. That one deliberately does
 * NOT ask about the mode, because accepting FROM a shortlist is exactly what a
 * shortlist is for; this one asks about nothing else first. Two controls, two
 * different acts, on the same row.
 */
export function canIncludeCandidate(
  offer: { mode: string; status: string },
  candidate: { status: string; workerActionable: boolean },
): boolean {
  return (
    offer.mode === 'manager_led' &&
    offer.status === 'open' &&
    candidate.status === 'offered' &&
    !candidate.workerActionable
  );
}

/**
 * What a scheduler reads beside a name on a `manager_led` shortlist.
 *
 * NOT rendered on an `open_shift` offer, where "they can answer this" is true of
 * everybody and saying it would be noise. `null` means say nothing.
 */
export function inclusionStandingText(
  offer: { mode: string },
  candidate: { workerActionable: boolean },
): string | null {
  if (offer.mode !== 'manager_led') return null;
  return candidate.workerActionable
    ? 'can answer this themselves'
    : 'shortlisted — has not been asked';
}

/** The message under a failed mutation, without a code and without a lie. */
export function messageOf(err: unknown): string {
  const message = (err as { message?: unknown } | null | undefined)?.message;
  return typeof message === 'string' && message.length > 0
    ? message
    : 'Something went wrong. Nothing was changed.';
}
